/**
 * THE TOOLBAR'S ACTION ROUTING, LIFTED OUT OF THE COMPONENT.
 *
 * <h2>Why it moved</h2>
 *
 * This switch was a plain local {@code const handleIconClick} inside {@code TabPanel}. It is the
 * single entry point for 47 actions and it was reachable from exactly one place — not exported,
 * not on a ref, with no {@code useImperativeHandle} anywhere near it. The 2026-10-24 architecture
 * audit named that the blocker for a Command Bus: every capability the toolbar has was trapped
 * behind a DOM click handler.
 *
 * <p>Nothing about the routing changed in the move. Same cases, same order, same bodies, same
 * {@code break} placement — only the source of each callback, which was a mixture of destructured
 * locals and {@code props.*} and is now one {@code handlers} object.
 *
 * <h2>`handlers` is `{...props}` plus four things props cannot carry</h2>
 *
 * The caller spreads its own props in, so all 47 callbacks map by construction rather than by a
 * hand-written list somebody has to keep in step. Four entries cannot come from props and are
 * supplied explicitly by {@code TabPanel}:
 *
 * <ul>
 *   <li>{@code onToggleAdminPanel} — the panel's own open state, local to the component;</li>
 *   <li>{@code clearOverlays} — from {@code ChartOverlayContext}, not a prop;</li>
 *   <li>{@code onOpenMoaFallback} — the MOA dialog's local open state, used when no
 *       {@code onOpenMOAStrategyDialog} prop was supplied;</li>
 *   <li>{@code drawingMode} — a VALUE rather than a callback, read by the drawing chain.</li>
 * </ul>
 *
 * <h2>THE THREE PRE-SWITCH BRANCHES CAME TOO, and that is the point</h2>
 *
 * {@code ADMIN_PANEL_ACTION}, {@code open_horizontal_line_dialog} and {@code clear_all_overlays}
 * are handled before the switch and return. Leaving them in the component would have made this a
 * HALF extraction: a Command Bus calling {@code dispatchToolbarAction} directly would route 47
 * actions and silently drop three, which is the shape of defect this repository keeps recording —
 * a capability that is present, reachable by one path, and absent from the other.
 *
 * <p>Their ORDER is load-bearing and is preserved verbatim. The admin-panel branch is first and
 * returns so it can never fall through into the {@code draw_*} chain below;
 * {@code TabPanel.routing.test.js} exists because that exact fall-through shipped once and turned
 * eight drawing tools into a modal.
 *
 * <h2>No return value, deliberately</h2>
 *
 * A Command Bus would like to know whether an action was recognised. Adding that means turning
 * every {@code break;} into a {@code return}, which changes the source shape
 * {@code TabPanel.routing.test.js} parses to prove the drawing chain is intact — so it is a
 * change to make WITH that test, not alongside a pure extraction. Phase 1 moves the code and
 * nothing else.
 */

import { ADMIN_PANEL_ACTION } from '../config/adminPanelTools';
import { orbForAction } from '../components/ctsToolbarButtons';
import { cycleForAction } from '../components/cycleToolbarButtons';
import { drawingModeAfterClick } from './textAnnotationSizes';

/**
 * Route one toolbar action to its handler.
 *
 * @param {string} action   the action string carried by the icon entry, the Admin Panel row, or
 *                          (from Phase 2) a Copilot command.
 * @param {object} handlers every callback the action might need, plus `isAdmin`, `studyMode` and
 *                          `drawingMode`. A missing callback means the action does NOTHING —
 *                          never a throw, and never a fall-through into the next case.
 */
export function dispatchToolbarAction(action, handlers) {
  const h = handlers || {};

  // THE PANEL'S OWN ICON. Handled before everything else and returning, so it can never fall
  // through into the draw_* chain below -- the failure TabPanel.routing.test.js exists for.
  if (action === ADMIN_PANEL_ACTION) {
    if (h.isAdmin && typeof h.onToggleAdminPanel === 'function') h.onToggleAdminPanel();
    return;
  }
  if (action === 'open_horizontal_line_dialog') {
    if (typeof h.onOpenHorizontalLineDialog === 'function') {
      h.onOpenHorizontalLineDialog();
    }
    return;
  }
  if (action === 'clear_all_overlays') {
    if (typeof h.clearOverlays === 'function') h.clearOverlays();
    /*
     * ⚠ AND THE CYCLE RUNS GO WITH THEM, or this clear does not stick.
     *
     * `clearOverlays` empties the overlay array; `cycleRuns` is separate state in App holding the
     * intervals a drawn cycle returned. `handleDeleteLastDrawing` (X1) has always purged it, with
     * the note that "the overlay array is a cache the effect refills" and that bands "reappeared
     * the moment the other cycle was drawn". Clear-all did NOT purge it, so X left PTA's run
     * behind and drawing PTB afterwards brought PTA's bands back.
     *
     * Found by verifying the two clear buttons before removing the header chips' ✕ -- which made
     * these the only routes to clearing, so a gap in one stopped being cosmetic.
     */
    if (typeof h.onClearCycleRuns === 'function') h.onClearCycleRuns();
    if (h.studyMode && typeof h.onExitStudyMode === 'function') {
      h.onExitStudyMode();
    }
    return;
  }
  switch (action) {
    case 'toggle_rsi_overlay':
      if (typeof h.onToggleRSI === 'function') h.onToggleRSI();
      break;
    case 'toggle_macd_overlay':
      if (typeof h.onToggleMACD === 'function') h.onToggleMACD();
      break;
    case 'open_controls':
    case 'draw_line':
      if (h.onOpenControlsDialog) h.onOpenControlsDialog();
      break;
    case 'open_planet_follow_study':
      if (h.onOpenPlanetFollowStudyDialog) h.onOpenPlanetFollowStudyDialog();
      break;
    case 'open_special_studies':
      if (h.onOpenAspectsAreaHighlightDialog) h.onOpenAspectsAreaHighlightDialog();
      break;
    case 'open_declination_study':
      if (h.onOpenDeclinationStudyDialog) h.onOpenDeclinationStudyDialog();
      break;
    case 'open_planetary_lines_study':
      if (h.onOpenPlanetaryLinesDialog) h.onOpenPlanetaryLinesDialog();
      break;
    case 'open_average_longitude_study':
      if (h.onOpenAverageLongitudeDialog) h.onOpenAverageLongitudeDialog();
      break;
    case 'open_visual_universe':
      if (h.onOpenVisualUniverseDialog) h.onOpenVisualUniverseDialog();
      break;
    case 'open_intraday_animation':
      if (h.onOpenIntradayAnimationDialog) h.onOpenIntradayAnimationDialog();
      break;
    case 'open_chart_comparison':
      if (h.onOpenChartComparisonDialog) h.onOpenChartComparisonDialog();
      break;
    case 'open_area_highlight_study':
      if (h.onOpenAreaHighlightDialog) h.onOpenAreaHighlightDialog();
      break;
    case 'open_planet_group_study':
      if (h.onOpenPlanetGroupDialog) h.onOpenPlanetGroupDialog();
      break;
    case 'open_strategy_report_study':
      if (h.onOpenStrategyReportStudyDialog) h.onOpenStrategyReportStudyDialog();
      break;
    case 'open_strategy_report_study_b':
      if (h.onOpenStrategyReportStudyBDialog) h.onOpenStrategyReportStudyBDialog();
      break;
    case 'open_aggregate_area_highlight_study':
      if (h.onOpenAggregateAreaHighlightDialog) h.onOpenAggregateAreaHighlightDialog();
      break;
    // New: open StrategyDesignDialog for 12th icon (index 11, AdjustIcon)
    case 'open_strategy_design_dialog':
      if (h.onOpenStrategyDesignDialog) h.onOpenStrategyDesignDialog();
      break;
    case 'open_strategy_2_design_dialog':
      if (h.onOpenStrategy2DesignDialog) h.onOpenStrategy2DesignDialog();
      break;
    case 'open_time_and_event_strategy_dialog':
      if (h.onOpenTimeAndEventStrategyDialog) h.onOpenTimeAndEventStrategyDialog();
      break;
    case 'open_moa_strategy_dialog':
      if (h.onOpenMOAStrategyDialog) h.onOpenMOAStrategyDialog();
      else if (typeof h.onOpenMoaFallback === 'function') h.onOpenMoaFallback();
      break;
    // The customer's own settings, not a study. Its own case rather than anywhere near the
    // draw_* chain, which shares one body and has caused a regression before.
    case 'open_configuration_dialog':
      if (typeof h.onOpenConfigurationDialog === 'function') {
        h.onOpenConfigurationDialog();
      }
      break;
    // UI ONLY FOR NOW, as specified: the messaging itself is the next step. The case exists so
    // the button is wired the moment a handler is passed - and so that until then it does
    // NOTHING rather than falling through into whatever case follows it, which is the failure
    // TabPanel.routing.test.js exists to catch.
    case 'open_messages_dialog':
      if (typeof h.onOpenMessagesDialog === 'function') {
        h.onOpenMessagesDialog();
      }
      break;
    // Its own case, and it does NOTHING without a handler rather than falling through into
    // whatever follows it — the failure TabPanel.routing.test.js exists to catch.
    case 'open_dynaman_simulator':
      if (typeof h.onOpenDynaManSimulator === 'function') {
        h.onOpenDynaManSimulator();
      }
      break;
    // Its own case, and it does NOTHING without a handler rather than falling through into
    // whatever follows it — the failure TabPanel.routing.test.js exists to catch.
    case 'launch_simulator':
      if (typeof h.onLaunchSimulator === 'function') {
        h.onLaunchSimulator();
      }
      break;
    // Its own case, and it does NOTHING without a handler rather than falling through into
    // whatever follows it — the failure TabPanel.routing.test.js exists to catch.
    case 'chart_analytica':
      if (typeof h.onChartAnalytica === 'function') {
        h.onChartAnalytica();
      }
      break;
    /*
     * THE MARKET SCANNER. Same handler the Admin Panel calls -- `handleRunMarketScanner` -- so
     * the toolbar and the panel cannot drift into two scans with different parameters.
     *
     * ⚠ NO ARGUMENTS FROM HERE. The panel passes (market, date) from its own two inputs; a
     * toolbar icon has neither, so it passes nothing and the handler applies the deployment's
     * market and the latest session. Reading a market out of the action string was the obvious
     * alternative and is the mistake the engine cases above are written to avoid: a mistyped
     * `run_market_scanner_uk` would resolve to a market nobody has.
     */
    case 'run_market_scanner':
      if (typeof h.onRunMarketScanner === 'function') {
        h.onRunMarketScanner();
      }
      break;
    // ── THE THREE ONE-CLICK ENGINES ─────────────────────────────────────────────────────
    //
    // One case each, carrying the provider name, rather than one case with the name parsed
    // out of the action string. A parsed name is a name that can be mistyped into silence:
    // `run_engine_pat` would resolve to a provider nobody has, the backend would refuse it,
    // and the reader would see an error about a provider they never chose.
    case 'run_engine_pta':
      if (typeof h.onRunEngine === 'function') h.onRunEngine('PTA');
      break;
    case 'run_engine_ptb':
      if (typeof h.onRunEngine === 'function') h.onRunEngine('PTB');
      break;
    case 'run_engine_cts':
      if (typeof h.onRunEngine === 'function') h.onRunEngine('CTS');
      break;
    // Its own case, doing NOTHING without a handler rather than falling through into whatever
    // follows it - the failure TabPanel.routing.test.js exists to catch.
    case 'open_portfolio_manager':
      if (typeof h.onOpenPortfolioManager === 'function') {
        h.onOpenPortfolioManager();
      }
      break;
    case 'open_planet_characteristics_dialog':
      if (h.onOpenPlanetCharacteristicsDialog) h.onOpenPlanetCharacteristicsDialog();
      break;
    case 'run_trendpulse':
      // Runs the study directly on the Custom preset. The configuration dialog is retained in the
      // codebase and still wired to onOpenAstroGraphDialog for administrative use; it is simply
      // no longer what this button does.
      if (h.onRunTrendPulse) h.onRunTrendPulse();
      else if (h.onOpenAstroGraphDialog) h.onOpenAstroGraphDialog();
      break;
    // A one-click study: no dialog, no drawing mode. Deliberately its own case rather than
    // anywhere near the draw_* chain, which shares one body and has caused a regression before.
    case 'spiral_reversal_scan':
      if (typeof h.onSpiralReversalScan === 'function') h.onSpiralReversalScan();
      break;
    // Also a one-click study, and kept beside the one it resembles rather than in the draw_*
    // chain, which shares one body and has caused a regression before.
    case 'toggle_simulate':
      if (typeof h.onToggleSimulate === 'function') h.onToggleSimulate();
      break;
    case 'run_cycle_long':
    case 'run_cycle_short':
      if (typeof h.onRunCycleBand === 'function') {
        h.onRunCycleBand(cycleForAction(action));
      }
      break;
    case 'cts_scan_1':
    case 'cts_scan_2':
    case 'cts_scan_3':
      // Runs immediately at the button's own orb. The orb dialog is retained in the codebase for
      // administrative use; it is simply no longer what these buttons open.
      //
      // The orb is read off the action rather than held in a closure, because the ICONS array is
      // module-level data and its entries carry no state - the action string IS the parameter.
      // orbForAction validates it, so a hand-typed action cannot dispatch an orb with no button.
      if (typeof h.onRunCts === 'function') h.onRunCts(orbForAction(action));
      break;
    case 'toggle_recent_fib_projections':
      if (typeof h.onToggleRecentFibProjections === 'function') h.onToggleRecentFibProjections();
      break;
    // All three anchor on the last clicked bar and all three TOGGLE, so a second press removes the
    // figure rather than drawing a duplicate on top of it — the recent-pivot projection's own
    // shape, and the only one that makes a panel button whose effect is already on the chart
    // honest.
    case 'toggle_sk360':
      if (typeof h.onToggleSk360 === 'function') h.onToggleSk360();
      break;
    case 'toggle_time_projection':
      if (typeof h.onToggleTimeProjection === 'function') h.onToggleTimeProjection();
      break;
    // ⚠ Unlike its two siblings, this one REFUSES an absent anchor rather than falling back to the
    // last bar. The panel disables the button until a bar is clicked; the builder enforces it too,
    // because a hidden control is a convenience and not a guard.
    case 'toggle_sk_grid':
      if (typeof h.onToggleSkGrid === 'function') h.onToggleSkGrid();
      break;
    case 'run_pivot_addition_series':
      if (typeof h.onRunPivotAdditionSeries === 'function') h.onRunPivotAdditionSeries();
      break;
    case 'run_fib_pivot_pairs':
      if (typeof h.onRunFibPivotPairs === 'function') h.onRunFibPivotPairs();
      break;
    case 'run_astrograph_patterns':
      if (h.onRunAstroGraphPatterns) h.onRunAstroGraphPatterns();
      break;
    case 'open_pattern_prediction_dialog':
      if (h.onOpenPatternPredictionDialog) h.onOpenPatternPredictionDialog();
      break;
    case 'sweep_screenshots':
      if (h.onSweepScreenshots) h.onSweepScreenshots();
      break;
    /*
     * ⚠ TWO CONTROLS THAT WERE PROP CALLS UNTIL 2026-11-01, and had to become routable.
     *
     * Both lived on the toolbar behind a bare `props.isAdmin &&` and invoked their handler
     * directly, so neither had an action the dispatcher knew. Moving them into the Admin Panel's
     * System tab meant giving them one: a panel row dispatches through `onAction`, which is this
     * function, so a tool with no case here renders a button that silently does nothing — the
     * routability failure `adminPanelTools.test.js` already guards every other panel tool against.
     */
    case 'open_daily_scan_viewer':
      if (h.onOpenDailyScanViewer) h.onOpenDailyScanViewer();
      break;
    case 'open_messages_panel':
      /* "Show", not "toggle": the toolbar button it replaced always opened the panel. */
      if (h.setMessagesOpen) h.setMessagesOpen(true);
      break;
    // ── OPERATOR FEATURES ──────────────────────────────────────────────────────────────────
    case 'evaluate_validation_lines':
      if (h.onEvaluateValidationLines) h.onEvaluateValidationLines();
      break;
    case 'copy_chart':
      if (h.onCopyChart) h.onCopyChart();
      break;
    case 'save_screenshot':
      if (h.onSaveScreenshot) h.onSaveScreenshot();
      break;
    case 'open_pattern_config_dialog':
      // The `[TabPanel]` prefix is kept verbatim although this file is no longer TabPanel: it
      // names the SUBSYSTEM a reader greps for, and rewriting it would change observable output
      // in a change whose whole claim is that nothing observable changed.
      console.log('[TabPanel] Zodiac Wheels icon clicked (open_pattern_config_dialog)');
      if (h.onOpenPatternConfigDialog) h.onOpenPatternConfigDialog();
      break;
    case 'vertical_lines_action':
      if (h.onVerticalLinesAction) h.onVerticalLinesAction();
      break;
    // Its own case, deliberately placed BEFORE the drawing-tool chain rather than inside it.
    // This opens a dialog; it does not arm a drawing mode. Sitting among the fall-through
    // labels, its body and break captured every tool listed above it.
    case 'open_fixed_cycle_dialog':
      if (typeof h.onOpenFixedCycleDialog === 'function') h.onOpenFixedCycleDialog();
      break;
    case 'draw_vertical_line':
    case 'draw_horizontal_line':
    case 'draw_text':
    case 'draw_vertical_full':
    case 'draw_circle_empty':
    case 'draw_circle_filled':
    case 'draw_trendline':
    case 'draw_rectangle':
    case 'draw_fib_time':
    case 'draw_fibonacci':
    case 'draw_extended_trendline':
    case 'draw_pitchfork':
      if (typeof h.onSetDrawingMode === 'function') {
        // Toggle off if already active — except draw_text, where a repeat click advances the
        // size cycle instead. See drawingModeAfterClick.
        h.onSetDrawingMode(drawingModeAfterClick(h.drawingMode, action));
      }
      break;
    default:
      break;
  }
}

export default dispatchToolbarAction;
